import { useNavigate } from 'react-router-dom';
import { Handshake } from 'lucide-react';
import type { CustomerRow } from '@/lib/crm-client';
import { ProjectStatusPill } from '@/components/ProjectStatusPill';
import { cn, formatDate } from '@/lib/utils';
import { ListenKarte } from './ListenKarte';
import { ZEILEN, heuteBeginn } from './useDashboardData';

/**
 * Die Kunden, bei denen gerade etwas läuft.
 *
 * Sortiert nach der nächsten Fälligkeit, nicht alphabetisch: gefragt ist, wo
 * als Nächstes etwas ansteht. Kunden ohne aktives Projekt bleiben draußen —
 * sie stehen in der Firmenliste („Kunden“ und „Ruht“), und hier wäre ihre Zeile leer.
 */
export function CustomersCard({ customers }: { customers: CustomerRow[] }) {
  const navigate = useNavigate();

  const laufend = customers
    .filter((k) => k.activeProjects > 0)
    .sort(nachFaelligkeit);
  const sichtbar = laufend.slice(0, ZEILEN);
  const grenze = heuteBeginn();

  return (
    <ListenKarte
      titel="Kunden & Projekte"
      icon={Handshake}
      mehr={{ label: 'Alle Kunden', onClick: () => navigate('/firmen?status=kunde') }}
      leer={
        customers.length === 0
          ? 'Noch keine Kunden — Firmen mit gewonnenem Deal lassen sich in der Firmenakte befördern.'
          : 'Kein Kunde hat gerade ein aktives Projekt.'
      }
      gesamt={laufend.length}
      gezeigt={sichtbar.length}
    >
      {sichtbar.map((k) => {
        const ueberfaellig = k.nextDueDate !== null && k.nextDueDate < grenze;
        return (
          <li key={k.id}>
            <button
              onClick={() =>
                navigate(`/firmen/${k.organizationId}`, { state: { from: '/' } })
              }
              className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-muted/40"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{k.organizationName}</p>
                <p className="truncate text-xs text-muted-foreground">
                  {k.activeProjects === 1
                    ? '1 aktives Projekt'
                    : `${k.activeProjects} aktive Projekte`}
                  {k.primaryContactName && ` · ${k.primaryContactName}`}
                </p>
              </div>

              {k.latestProjectStatus && (
                <span className="hidden shrink-0 sm:block">
                  <ProjectStatusPill status={k.latestProjectStatus} />
                </span>
              )}

              <span
                className={cn(
                  'shrink-0 text-xs',
                  ueberfaellig ? 'font-medium text-danger' : 'text-muted-foreground',
                )}
                title="Nächste Fälligkeit"
              >
                {formatDate(k.nextDueDate)}
              </span>
            </button>
          </li>
        );
      })}
    </ListenKarte>
  );
}

/** Nächste Fälligkeit zuerst; wer keine hat, ans Ende. */
function nachFaelligkeit(a: CustomerRow, b: CustomerRow): number {
  if (a.nextDueDate === b.nextDueDate) return 0;
  if (a.nextDueDate === null) return 1;
  if (b.nextDueDate === null) return -1;
  return a.nextDueDate < b.nextDueDate ? -1 : 1;
}
